import Konva from "konva";
import { Position } from "./konva/position";

// Transformationskonstanten für Stand-Positionen relativ zum Hintergrund
// Bei Hintergrundänderungen nur diese Werte anpassen!
const POSITION_OFFSET_X = 0;    // Horizontale Verschiebung aller Stände
const POSITION_OFFSET_Y = -150; // Vertikale Verschiebung aller Stände (negativ = nach oben)

// ============================================================================
// SCHUTZMECHANISMUS: Stände 1-10 sind manuell korrekt positioniert.
// Diese Koordinaten dürfen NICHT verändert werden!
// ============================================================================
const PROTECTED_STANDS_1_TO_10 = Object.freeze({
    1: { x: 1330, y: 10, w: 100, h: 100 },
    2: { x: 1330, y: -135, w: 100, h: 140 },
    3: { x: 1330, y: -240, w: 100, h: 100 },
    4: { x: 1330, y: -420, w: 100, h: 175 },
    5: { x: 1100, y: -80, w: 100, h: 190 },
    6: { x: 1100, y: -225, w: 100, h: 140 },
    7: { x: 1100, y: -420, w: 100, h: 190 },
    8: { x: 995, y: -80, w: 100, h: 190 },
    9: { x: 995, y: -225, w: 100, h: 140 },
    10: { x: 995, y: -420, w: 100, h: 190 },
});

// Dev-Mode Assertion: Warnt, falls geschützte Werte verändert wurden
function assertProtectedStandUnchanged(standId: number, x: number, y: number, w: number, h: number): void {
    if (process.env.NODE_ENV === 'development' && standId >= 1 && standId <= 10) {
        const expected = PROTECTED_STANDS_1_TO_10[standId as keyof typeof PROTECTED_STANDS_1_TO_10];
        if (expected && (x !== expected.x || y !== expected.y || w !== expected.w || h !== expected.h)) {
            console.error(`⚠️ WARNUNG: Stand ${standId} wurde verändert!`);
            console.error(`  Erwartet: x=${expected.x}, y=${expected.y}, w=${expected.w}, h=${expected.h}`);
            console.error(`  Aktuell:  x=${x}, y=${y}, w=${w}, h=${h}`);
        }
    }
}

export class App {
    currentScale = 1;


    stage: Konva.Stage | undefined;
    layer: Konva.Layer | undefined;
    konvaContainer: HTMLElement | null | undefined;
    positions: Position[] = [];
    stuffs: Position[] = [];
    events: any[] = [];

    dragMode: boolean = false;
    dragElement: Position | undefined = undefined;

    private lastPinchDist: number = 0;
    private resizeTimeout: any = null;
    private boundHandleResize: () => void;

    constructor() {
        this.boundHandleResize = this.handleResize.bind(this);
        this.start();
        window.addEventListener('resize', this.boundHandleResize);
    }

    private handleResize() {
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            if (!this.stage || !this.konvaContainer) return;
            this.stage.width(this.konvaContainer.offsetWidth);
            this.stage.height(this.konvaContainer.offsetHeight);
        }, 150);
    }

    destroy() {
        window.removeEventListener('resize', this.boundHandleResize);
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    }

    // Hilfsmethode: Erstellt Position mit automatischer Offset-Transformation
    createPosition(x: number, y: number, width: number, height: number, label?: string, bgColor?: string, isInfo?: boolean, labelVertical?: boolean, textColor?: string, fontSize?: number): Position {
        return new Position(
            this,
            x + POSITION_OFFSET_X,
            y + POSITION_OFFSET_Y,
            width,
            height,
            label,
            bgColor,
            isInfo,
            labelVertical,
            textColor,
            fontSize
        );
    }

    on(name: string, _func: (args: any) => void) {
        //console.log(name, 'created');
        this.events.push({ name: name, func: _func });
    }

    emit(name: string, data: any = undefined) {
        const event = this.events.find((evt) => evt.name === name);
        if (event === undefined) return console.log(`event ${name} not found!`);

        return event.func(data);
    }

    start() {
        this.konvaContainer = document.getElementById('container');
        if (this.konvaContainer == null) return //console.log('cannot find konva container!');

        this.stage = new Konva.Stage({
            container: 'container',
            width: this.konvaContainer.offsetWidth,
            height: this.konvaContainer.offsetHeight,
            draggable: true,
            dragDistance: 5,
        });

        this.layer = new Konva.Layer();
        this.stage.add(this.layer);

        console.log('App Started');

        Konva.Image.fromURL('/plan.png', (node: any) => {
            if (this.layer === undefined) return;

            console.log(node.width(), node.height());

            node.x(Number('-' + node.width() / 2) + -400);
            node.y(Number('-' + node.height() / 2) - 380);
            node.id("image");
            this.layer.add(node);

            // HALLE 1
            // --------------------------------------------------------------------------------------
            //Reihe 6 (ganz rechts) Nummer 01 - 04
            this.positions.push(this.createPosition(1330, 10, 100, 100));          // 01
            this.positions.push(this.createPosition(1330, -135, 100, 140));        // 02
            this.positions.push(this.createPosition(1330, -240, 100, 100));        // 03
            this.positions.push(this.createPosition(1330, -420, 100, 175));        // 04
            // --------------------------------------------------------------------------------------
            //Reihe 5 Rechts Nummer 05 - 07
            this.positions.push(this.createPosition(1150, -80, 100, 190));         // 05
            this.positions.push(this.createPosition(1150, -225, 100, 140));        // 06
            this.positions.push(this.createPosition(1150, -420, 100, 190));        // 07
            //Reihe 5 Links Nummer 08 - 10
            this.positions.push(this.createPosition(1045, -80, 100, 190));         // 08
            this.positions.push(this.createPosition(1045, -225, 100, 140));        // 09
            this.positions.push(this.createPosition(1045, -420, 100, 190));        // 10
            // --------------------------------------------------------------------------------------
            // KORRIGIERT: Reihe 4 Rechts Nummer 11 - 13
            this.positions.push(this.createPosition(815, -30, 100, 140));          // 11
            this.positions.push(this.createPosition(815, -225, 100, 190));         // 12
            this.positions.push(this.createPosition(815, -420, 100, 190));         // 13
            // KORRIGIERT: Reihe 4 Links Nummer 14 - 16
            this.positions.push(this.createPosition(710, -80, 100, 190));          // 14
            this.positions.push(this.createPosition(710, -275, 100, 190));         // 15
            this.positions.push(this.createPosition(710, -420, 100, 140));         // 16
            // --------------------------------------------------------------------------------------
            // KORRIGIERT: Reihe 3 Rechts Nummer 17 - 19
            this.positions.push(this.createPosition(480, -80, 100, 190));          // 17
            this.positions.push(this.createPosition(480, -275, 100, 190));         // 18
            this.positions.push(this.createPosition(480, -420, 100, 140));         // 19
            // KORRIGIERT: Reihe 3 Links Nummer 20 - 22
            this.positions.push(this.createPosition(375, -80, 100, 190));          // 20
            this.positions.push(this.createPosition(375, -225, 100, 140));         // 21
            this.positions.push(this.createPosition(375, -420, 100, 190));         // 22
            // --------------------------------------------------------------------------------------
            // KORRIGIERT: Reihe 2 Rechts Nummer 23 - 25
            this.positions.push(this.createPosition(145, -80, 100, 190));          // 23
            this.positions.push(this.createPosition(145, -225, 100, 140));         // 24
            this.positions.push(this.createPosition(145, -420, 100, 190));         // 25
            // KORRIGIERT: Reihe 2 Links Nummer 26 - 28
            this.positions.push(this.createPosition(40, -80, 100, 190));           // 26
            this.positions.push(this.createPosition(40, -275, 100, 190));          // 27
            this.positions.push(this.createPosition(40, -420, 100, 140));          // 28
            // --------------------------------------------------------------------------------------
            // KORRIGIERT: Reihe 1 Links Nummer 29 - 31
            this.positions.push(this.createPosition(-100, 30, 100, 170));          // 29
            this.positions.push(this.createPosition(-100, -75, 100, 100));         // 30
            this.positions.push(this.createPosition(-100, -520, 100, 280));        // 31
            // --------------------------------------------------------------------------------------
            //Verbindungsbereich 32 - 35
            this.positions.push(this.createPosition(-450, 600, 500, 200));         // 32
            this.positions.push(this.createPosition(-650, 300, 100, 200));         // 33
            this.positions.push(this.createPosition(-700, -75, 150, 250));         // 34
            this.positions.push(this.createPosition(-650, -300, 100, 140));        // 35

            // HALLE 2
            // -----------------------------------------------------------------------------------------
            // Obere Reihe Nummer 36 - 42
            this.positions.push(this.createPosition(-1390, -650, 200, 100));        // 36
            this.positions.push(this.createPosition(-1605, -650, 200, 100));        // 37
            this.positions.push(this.createPosition(-1710, -650, 100, 100));        // 38
            this.positions.push(this.createPosition(-1915, -650, 200, 100));        // 39
            this.positions.push(this.createPosition(-2020, -650, 100, 100));        // 40
            this.positions.push(this.createPosition(-2175, -650, 150, 100));        // 41
            this.positions.push(this.createPosition(-2360, -650, 180, 100));        // 42

            // -----------------------------------------------------------------------------------------
            // Linke Reihe Nummer 43 - 47
            this.positions.push(this.createPosition(-2365, -410, 100, 180));        // 43
            this.positions.push(this.createPosition(-2365, -225, 100, 100));        // 44
            this.positions.push(this.createPosition(-2365, -120, 100, 180));        // 45
            this.positions.push(this.createPosition(-2365, 65, 100, 150));          // 46
            this.positions.push(this.createPosition(-1560, -400, 200, 100));        // 47

            // -----------------------------------------------------------------------------------------
            // Untere Reihe Nummer 48 - 51
            this.positions.push(this.createPosition(-2100, 230, 200, 100));         // 48
            this.positions.push(this.createPosition(-1895, 230, 150, 100));         // 49
            this.positions.push(this.createPosition(-1740, 230, 150, 100));         // 50
            this.positions.push(this.createPosition(-1585, 230, 200, 100));         // 51

            // -----------------------------------------------------------------------------------------
            // Rechte Reihe Nummer 52 - 55
            this.positions.push(this.createPosition(-1240, 105, 100, 150));         // 52
            this.positions.push(this.createPosition(-1240, -100, 100, 200));        // 53
            this.positions.push(this.createPosition(-1240, -255, 100, 150));        // 54
            this.positions.push(this.createPosition(-1240, -410, 100, 150));        // 55

            // -----------------------------------------------------------------------------------------
            // Innere Reihe 1: 56, 57, 58, 59
            this.positions.push(this.createPosition(-5000, -400, 200, 100));        // 56 (außerhalb des sichtbereichs da nicht benöitgt)
            this.positions.push(this.createPosition(-1765, -400, 200, 100));        // 57
            this.positions.push(this.createPosition(-1970, -400, 200, 100));        // 58
            this.positions.push(this.createPosition(-2175, -400, 200, 100));        // 59

            // -----------------------------------------------------------------------------------------
            // Innere Reihe 2: 60, 61, 62
            this.positions.push(this.createPosition(-1520, -295, 160, 160));        // 60
            this.positions.push(this.createPosition(-1685, -295, 160, 160));        // 61
            this.positions.push(this.createPosition(-1890, -295, 200, 160));        // 62
            this.positions.push(this.createPosition(-1995, -235, 100, 100));        // 63
            this.positions.push(this.createPosition(-2175, -235, 175, 100));        // 64

            // -----------------------------------------------------------------------------------------
            // Innere Reihe 3: 65, 66, 67
            this.positions.push(this.createPosition(-1560, -80, 200, 100));         // 65
            this.positions.push(this.createPosition(-1765, -80, 200, 100));         // 66
            this.positions.push(this.createPosition(-1970, -80, 200, 100));         // 67
            this.positions.push(this.createPosition(-2175, -80, 200, 100));         // 68

            // -----------------------------------------------------------------------------------------
            // Innere Reihe 4: 69, 70, 71, 72
            this.positions.push(this.createPosition(-1505, 25, 145, 100));          // 69
            this.positions.push(this.createPosition(-1660, 25, 150, 100));          // 70
            this.positions.push(this.createPosition(-1815, 25, 150, 100));          // 71
            this.positions.push(this.createPosition(-1970, 25, 150, 100));          // 72
            this.positions.push(this.createPosition(-2175, 25, 200, 100));          // 73

            this.positions.push(this.createPosition(415, 330, 100, 100));           // 74


            // Sonstiges / Aussen

            // ---------------------------------------------------------------------------------------------------
            // Halle 1
            // HINWEIS: IMBISS, INFO, EINGANG Labels und alle Pfeile/WC-Icons sind bereits in plan.png enthalten
            // und werden daher nicht mehr separat gerendert (entfernt wegen Dopplung)
            this.positions.push(this.createPosition(-450, -75, 300, 600, "65a"));   // 65a / 90

            // Imbiss-Bereich (unsichtbar, nur klickbar)
            const foodPosition = this.createPosition(550, -615, 400, 120, 'food');
            foodPosition.box?.opacity(0);
            foodPosition.text?.opacity(0);
            foodPosition.box?.stroke('');
            foodPosition.box?.strokeWidth(0);
            foodPosition.canHovered = false;
            this.stuffs.push(foodPosition);

            // ---------------------------------------------------------------------------------------------------

            Konva.Image.fromURL('/logo_new.png', (node: any) => {
                if (!this.layer) return;

                node.x(-1440);
                node.y(-1450);
                node.width(1500);
                node.height(600);
                node.id("image-logo-white");
                this.layer.add(node);
            });

            this.layer.add(new Konva.Text({
                x: -1365,
                y: -1030,
                text: 'www.oberkircher-berufsinfomesse.de',
                fontSize: 50,
                fill: '#9f9f9f',
                fontFamily: 'Roboto'
            }));

            /*
            this.layer.add(new Konva.Text({
                x: -80,
                y: -870,
                text: 'HALLE 1',
                fontSize: 150,
                fill: '#9f9f9f',
                fontFamily: 'Roboto'
            }));
            this.layer.add(new Konva.Text({
                x: -2000,
                y: -870,
                text: 'HALLE 2',
                fontSize: 150,
                fill: '#9f9f9f',
                fontFamily: 'Roboto'
            }));
             */

            if (!this.stage) return;

            const offset = 100;
            this.stage.absolutePosition({ x: (this.stage.width() / 2) + offset, y: (this.stage.height() / 2) + offset });
            //this.stage.absolutePosition({x: -200, y: 200});
            this.zoom(1);
            this.onMouseWheel(undefined);
        });

        //this.stage.on('wheel', this.onMouseWheel.bind(this));

        // Pinch-to-Zoom für Touch-Geräte
        this.stage.on('touchmove', (e) => {
            e.evt.preventDefault();
            const touch1 = e.evt.touches[0];
            const touch2 = e.evt.touches[1];
            if (!touch1 || !touch2) return;

            const dist = Math.sqrt(
                Math.pow(touch2.clientX - touch1.clientX, 2) +
                Math.pow(touch2.clientY - touch1.clientY, 2)
            );

            if (!this.lastPinchDist) {
                this.lastPinchDist = dist;
                return;
            }

            const scale = this.currentScale * (dist / this.lastPinchDist);
            const clampedScale = Math.max(0.2, Math.min(2, scale));
            this.zoom(clampedScale);
            this.emit('zoom', clampedScale * 100);
            this.lastPinchDist = dist;
        });

        this.stage.on('touchend', () => {
            this.lastPinchDist = 0;
        });
    }

    onMouseWheel(event: any) {
        if (!event) return this.emit('zoom', (this.currentScale * 100));

        if (event.evt.deltaY < 0) {
            this.currentScale += .1;
            //console.log('up');
        } else {
            //console.log('down');
            this.currentScale -= .1;
        }

        this.emit('zoom', (this.currentScale * 100));
    }

    zoom(scale: number = -1) {
        if (this.stage === undefined) return;


        if (scale === -1)
            return this.stage.scale({ x: 1, y: 1 });

        //if ( scale <= 0.4 ) scale = 0.4;
        //if ( scale >= 2 ) scale = 2;

        this.currentScale = scale;

        return this.stage.scale({ x: scale, y: scale });
    }
}